import React from "react";
import { GlideCarousel } from "../atoms/glide/";
import get from "lodash/get";
import { ResponsiveHeroImage, ReviewRating, LazyLoadImages, wrapCollectionLayout } from "@quintype/components";
import { CustomLink } from "../custom-link";
import { CollectionName } from "../atoms/collection-name/index.js";
import { ImageFallback } from "../basic/image-fallback.js";
import { altTagOfHeroImages, isExternalStory, isEager, getRowMetadata } from "../../utils";
import { breakpoint } from "../../../utils/breakpoint";
import PropTypes from "prop-types";

function getSlidesEager(index, eager) {
  if (breakpoint("tablet")) {
    return index === 0 ? eager : false;
  }
  return index >= -1 && index <= 1 ? eager : false;
}

function FullscreenLinearGallerySliderBase({ collection, stories, config, associatedMetadata, index }) {
  const resize = () => {
    if (resizeDone === false) {
      global.dispatchEvent(new global.Event("resize"));
      resizeDone = true;
    }
  };
  const desktopOptions = {
    afterSlide: resize,
    perView: 3,
    arrow: true,
    dot: "",
    autoplay: 5000,
    gap: 21,
    peek: {
      before: "130px",
      after: "130px"
    },
    rtl: get(config, ["theme-attributes", "language_direction"]) === "right"
  };

  const mobileOptions = {
    afterSlide: resize,
    perView: 1,
    arrow: true,
    dot: "",
    autoplay: 5000,
    gap: 21,
    peek: {
      before: "80px",
      after: "80px"
    },
    rtl: get(config, ["theme-attributes", "language_direction"]) === "right"
  };

  const options = breakpoint("tablet") ? { ...mobileOptions } : { ...desktopOptions };

  const pbAheadConfig = getRowMetadata(config, associatedMetadata);

  const { show_collection_title: collectionName = true } = pbAheadConfig;

  if (!stories) {
    return null;
  }

  const eager = isEager(index, true);
  let resizeDone = false;

  const SliderWrapper = (
    <GlideCarousel {...options} className="linear-gallery-slider__slides">
      {stories.map((story, index) => {
        const initialVisibleSlidesEager = getSlidesEager(index, eager);
        return (
          <div key={`${story.id}-${index}`} className="linear-gallery-slider__slider-content">
            <SliderItem eager={initialVisibleSlidesEager} story={story} />
          </div>
        );
      })}
    </GlideCarousel>
  );

  return (
    <div className="linear-gallery-slider component-wrapper-fluid" data-test-id="linear-gallery-slider-component">
      <div className="linear-gallery-slider__heading">
        {collectionName ? (
          <CollectionName textColor="white" name={collection.name} config={config} section={collection} />
        ) : null}
      </div>
      {SliderWrapper}
    </div>
  );
}
FullscreenLinearGallerySliderBase.propTypes = {
  collection: PropTypes.object,
  stories: PropTypes.array,
  config: PropTypes.object,
  associatedMetadata: PropTypes.object,
  index: PropTypes.number
};

function SliderItem({ story, eager = false }) {
  const isReviewStory = story["story-template"] === "review";
  return (
    <CustomLink href={"" + story.url} externalLink={isExternalStory(story)}>
      {story["hero-image-s3-key"] ? (
        <figure className={`linear-gallery-slider__image--${story["story-template"]}`}>
          <LazyLoadImages>
            <ResponsiveHeroImage
              story={story}
              aspectRatio={[4, 3]}
              alt={altTagOfHeroImages(story)}
              defaultWidth={480}
              widths={[250, 420]}
              imgParams={{ auto: ["format", "compress"], fit: "max" }}
              sizes="(max-width: 500px) 23vw, (max-width: 768px) 25vw, 50vw"
              eager={true}
            />
          </LazyLoadImages>
        </figure>
      ) : (
        <ImageFallback story={story} />
      )}
      <p
        className="linear-gallery-slider__item-title"
        dangerouslySetInnerHTML={{ __html: story.headline }}
        data-test-id="fullscreen-linear-gallery-headline"
      />
      {isReviewStory && <ReviewRating value={get(story, ["metadata", "review-rating", "value"], "0")} />}
    </CustomLink>
  );
}
SliderItem.propTypes = {
  story: PropTypes.object,
  eager: PropTypes.bool
};

export const FullscreenLinearGallerySlider = wrapCollectionLayout(FullscreenLinearGallerySliderBase);
